import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import * as db from "@/lib/db.server";

/* ---------- public reads for the SSR page ----------
 * Split out of competitor-seo.functions.ts so the public `alternatives.$`
 * route (whose loader calls getCompetitorPageBySlug) does not statically pull
 * in the admin server fns' `.middleware([requireAdmin])` reference, which keeps
 * access.server (→ pg) alive in the client bundle and trips import-protection.
 */

export const getCompetitorPageBySlug = createServerFn({ method: "GET" })
  .inputValidator((input) => z.object({ slug: z.string().min(2).max(200) }).parse(input))
  .handler(async ({ data }) => {
    const row = await db.one<{
      id: string;
      slug: string;
      page_kind: string;
      competitor: string;
      specialty: string | null;
      city: string | null;
      state: string | null;
      provider_count: number | null;
      is_indexable: boolean;
      noindex_reason: string | null;
      manual_override: string | null;
      h1: string | null;
      title: string | null;
      meta_description: string | null;
      intro_md: string | null;
      comparison_md: string | null;
      faq_json: Array<{ q: string; a: string }> | null;
      last_refreshed_at: string;
    }>(
      `SELECT id, slug, page_kind, competitor, specialty, city, state, provider_count,
              is_indexable, noindex_reason, manual_override, h1, title, meta_description,
              intro_md, comparison_md, faq_json, last_refreshed_at
         FROM public.competitor_pages
        WHERE slug = $1`,
      [data.slug],
    );
    if (!row) return { page: null, providers: [] as Array<{ id: string }> };

    let providers: Array<{
      id: string;
      public_id: string | null;
      name: string;
      specialty: string;
      city: string;
      state: string | null;
      rating: number | null;
      google_review_count: number;
    }> = [];
    if (row.page_kind === "city") {
      const links = await db.query<{ provider_id: string; rank: number }>(
        `SELECT provider_id, rank
           FROM public.competitor_page_providers
          WHERE competitor_page_id = $1
          ORDER BY rank ASC`,
        [row.id],
      );
      const ids = links.map((l) => l.provider_id);
      if (ids.length > 0) {
        const provs = await db.query<{
          id: string;
          public_id: string | null;
          name: string;
          specialty: string;
          city: string;
          state: string | null;
          rating: number | null;
          google_review_count: number;
        }>(
          `SELECT id, public_id, name, specialty, city, state, rating, google_review_count
             FROM public.providers
            WHERE id = ANY($1)`,
          [ids],
        );
        const order = new Map(ids.map((id, i) => [id, i]));
        providers = provs.sort(
          (a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0),
        );
      }
    }
    return { page: row, providers };
  });

export const listIndexableCompetitorSlugs = createServerFn({ method: "GET" })
  .inputValidator((input) => z.object({ kind: z.enum(["core_competitor", "specialty", "city"]) }).parse(input))
  .handler(async ({ data }) => {
    let kinds: string[];
    if (data.kind === "core_competitor") kinds = ["core", "competitor"];
    else if (data.kind === "specialty") kinds = ["specialty"];
    else kinds = ["city"];
    const rows = await db.query<{ slug: string; last_refreshed_at: string }>(
      `SELECT slug, last_refreshed_at
         FROM public.competitor_pages
        WHERE is_indexable = $1
          AND page_kind = ANY($2)
        ORDER BY slug ASC`,
      [true, kinds],
    );
    return { rows };
  });
